/**
 * Login Page
 * CCH Care - Version 1.1
 * 
 * Matches CCH Labs login design:
 * - Dark background with nav bar
 * - Centered card with icon + welcome text
 * - Email + password fields
 * - Show/hide password toggle
 * - Forgot password + Start Free Trial links
 * - 2FA flow (setup and verify)
 */

import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from './useAuth';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, verify2FA, error, clearError } = useAuth();

  const [step, setStep] = useState('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const sessionExpired = searchParams.get('expired') === '1';

  async function handleCredentials(e) {
    e.preventDefault();
    clearError();
    setSubmitting(true);
    try {
      const data = await login(email, password);
      if (data.requires_2fa_setup) {
        setTempToken(data.temp_token);
        setQrCodeUrl(data.qr_code_url);
        setTotpSecret(data.secret);
        setStep('2fa_setup');
      } else if (data.requires_2fa) {
        setTempToken(data.temp_token);
        setStep('2fa_verify');
      }
    } catch (e) {
      // Error set by useAuth
    } finally {
      setSubmitting(false);
    }
  }

  async function handleVerify2FA(e) {
    e.preventDefault();
    clearError();
    setSubmitting(true);
    try {
      await verify2FA(tempToken, code);
      navigate('/dashboard');
    } catch (e) {
      setCode('');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0B1929', color: '#fff', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Inter", sans-serif' }}>
      
      {/* Top Nav */}
      <nav style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 32px', maxWidth: 1200, margin: '0 auto',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', color: '#fff' }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, background: '#1B6B93',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18,
          }}>🏥</div>
          <span style={{ fontSize: 18, fontWeight: 800 }}>CCH Care</span>
        </Link>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ color: '#93C5FD', fontSize: 14, fontWeight: 600 }}>Login</span>
          <Link to="/get-started" style={{
            padding: '10px 24px', background: '#2563EB', border: 'none',
            borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 700,
            textDecoration: 'none',
          }}>Start Free Trial</Link>
        </div>
      </nav>

      {/* Login Card */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: 'calc(100vh - 70px)', padding: 24,
      }}>
        <div style={{ width: '100%', maxWidth: 460 }}>

          {/* Icon + Welcome */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{
              width: 64, height: 64, borderRadius: 16, background: '#1B6B93',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 32, marginBottom: 16, boxShadow: '0 4px 16px rgba(27, 107, 147, 0.3)',
            }}>🏥</div>
            <h1 style={{ fontSize: 28, fontWeight: 800, marginBottom: 6 }}>
              {step === 'credentials' ? 'Welcome back' : 'Two-Factor Authentication'}
            </h1>
            <p style={{ color: '#64748B', fontSize: 15 }}>
              {step === 'credentials' ? 'Sign in to your account' 
                : step === '2fa_setup' ? 'Set up your authenticator app'
                : 'Enter your verification code'}
            </p>
          </div>

          {/* Session Expired */}
          {sessionExpired && step === 'credentials' && (
            <div style={{
              padding: '12px 16px', borderRadius: 10, marginBottom: 20,
              background: 'rgba(230, 126, 34, 0.15)', border: '1px solid rgba(230, 126, 34, 0.3)',
              color: '#F9A825', fontSize: 14, fontWeight: 500, textAlign: 'center',
            }}>
              Your session has expired. Please sign in again.
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{
              padding: '12px 16px', borderRadius: 10, marginBottom: 20,
              background: 'rgba(231, 76, 60, 0.15)', border: '1px solid rgba(231, 76, 60, 0.3)',
              color: '#FC8181', fontSize: 14, lineHeight: 1.5,
            }}>{error}</div>
          )}

          {/* Card */}
          <div style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 16, padding: 32,
          }}>

            {/* Step 1: Credentials */}
            {step === 'credentials' && (
              <form onSubmit={handleCredentials}>
                <div style={{ marginBottom: 20 }}>
                  <label style={labelStyle}>Email Address</label>
                  <input
                    type="email" value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@facility.com"
                    required autoComplete="email" autoFocus
                    style={inputStyle}
                  />
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={labelStyle}>Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? 'text' : 'password'} value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      required autoComplete="current-password"
                      style={inputStyle}
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      style={{
                        position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)',
                        background: 'none', border: 'none', color: '#64748B',
                        cursor: 'pointer', fontSize: 18, padding: 4,
                      }}
                      tabIndex={-1}
                    >{showPassword ? '🙈' : '👁️'}</button>
                  </div>
                </div>
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  marginBottom: 24, fontSize: 14,
                }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#94A3B8', cursor: 'pointer' }}>
                    <input type="checkbox" style={{ accentColor: '#2563EB' }} />
                    Remember me
                  </label>
                  <Link to="/forgot-password" style={{ color: '#60A5FA', fontWeight: 600, textDecoration: 'none' }}>
                    Forgot password?
                  </Link>
                </div>
                <button type="submit" disabled={submitting} style={{
                  ...btnPrimary, opacity: submitting ? 0.7 : 1,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                }}>
                  {submitting ? 'Signing In...' : 'Sign In'}
                </button>
                <div style={{ textAlign: 'center', marginTop: 20, fontSize: 14, color: '#64748B' }}>
                  Don't have an account?{' '}
                  <Link to="/get-started" style={{ color: '#60A5FA', fontWeight: 600, textDecoration: 'none' }}>
                    Start Free Trial
                  </Link>
                </div>
              </form>
            )}

            {/* Step 2a: 2FA Setup */}
            {step === '2fa_setup' && (
              <form onSubmit={handleVerify2FA}>
                <div style={{
                  textAlign: 'center', marginBottom: 24, padding: 20,
                  background: 'rgba(27, 107, 147, 0.1)', borderRadius: 12,
                  border: '1px solid rgba(27, 107, 147, 0.2)',
                }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#93C5FD', marginBottom: 12 }}>
                    Scan this QR code with your authenticator app:
                  </div>
                  {qrCodeUrl && (
                    <img src={qrCodeUrl} alt="2FA QR Code" 
                      style={{ width: 180, height: 180, borderRadius: 8, background: '#fff', padding: 8 }} />
                  )}
                  <div style={{ marginTop: 12, fontSize: 12, color: '#64748B' }}>Or enter manually:</div>
                  <div style={{
                    marginTop: 6, padding: '8px 16px', background: 'rgba(255,255,255,0.06)',
                    borderRadius: 8, fontFamily: 'monospace', fontSize: 15,
                    fontWeight: 700, letterSpacing: '0.1em',
                  }}>{totpSecret}</div>
                </div>
                <div style={{ marginBottom: 24 }}>
                  <label style={labelStyle}>Enter the 6-digit code from your app</label>
                  <input type="text" value={code} autoFocus maxLength={6} inputMode="numeric"
                    onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000" required
                    style={{ ...inputStyle, textAlign: 'center', fontSize: 28, fontWeight: 700, letterSpacing: '0.3em' }}
                  />
                </div>
                <button type="submit" disabled={submitting || code.length !== 6} style={{
                  ...btnPrimary, opacity: (submitting || code.length !== 6) ? 0.5 : 1,
                }}>
                  {submitting ? 'Verifying...' : 'Verify & Continue'}
                </button>
              </form>
            )}

            {/* Step 2b: 2FA Verify */}
            {step === '2fa_verify' && (
              <form onSubmit={handleVerify2FA}>
                <div style={{ textAlign: 'center', marginBottom: 24, color: '#94A3B8', fontSize: 15 }}>
                  Enter the 6-digit code from your authenticator app.
                </div>
                <div style={{ marginBottom: 24 }}>
                  <input type="text" value={code} autoFocus maxLength={6} inputMode="numeric"
                    onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000" required
                    style={{ ...inputStyle, textAlign: 'center', fontSize: 28, fontWeight: 700, letterSpacing: '0.3em' }}
                  />
                </div>
                <button type="submit" disabled={submitting || code.length !== 6} style={{
                  ...btnPrimary, opacity: (submitting || code.length !== 6) ? 0.5 : 1,
                }}>
                  {submitting ? 'Verifying...' : 'Verify'}
                </button>
                <button type="button"
                  onClick={() => { setStep('credentials'); setCode(''); clearError(); }}
                  style={{
                    width: '100%', marginTop: 12, padding: 14, fontSize: 14,
                    fontWeight: 600, border: 'none', borderRadius: 10,
                    background: 'transparent', color: '#64748B', cursor: 'pointer',
                  }}
                >← Back to Sign In</button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const labelStyle = {
  display: 'block', fontSize: 14, fontWeight: 600, color: '#CBD5E1', marginBottom: 8,
};

const inputStyle = {
  width: '100%', padding: '14px 16px', fontSize: 15,
  border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10,
  background: 'rgba(255,255,255,0.06)', color: '#fff',
  outline: 'none', fontFamily: 'inherit',
};

const btnPrimary = {
  width: '100%', padding: 16, border: 'none', borderRadius: 10,
  background: '#2563EB', color: '#fff', fontSize: 16, fontWeight: 700,
};
